import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from "react-redux";
import {useSearchParams} from "react-router-dom";
import {useTypedSelector} from "../redux/Hooks/storeSelectors";
import {
    selectCurrentProjectId,
    selectPresetsById,
    selectPresetType,
    selectProjectPresets,
    selectProjectRuns,
    selectRunsById,
    selectSelectedPresetId,
    selectSelectedRunId
} from "../redux/selectors/selectors";
import {isErrorResponse} from "../utils/utils";
import {getSessionStorageItem, updateSearchParam} from "../utils/storageUtils";
import {
    addedPresets,
    addedRuns,
    ProjectPreset,
    setSelectedPreset,
    setSelectedRunId
} from "../redux/reducers/projectsReducer";
import {PublicTransitPresetEntity} from "../api/entities/replancity_PublicTransitPreset";
import {presetsApi} from "../api/presetsApi/presetsApi";
import {ApiError} from "../api/RestClient";
import {CopyPresetFnArgs} from "../api/presetsApi/types";
import {entityApi} from "../api/entityApi";
import {RunTaskEntity} from "../api/entities/replancity_RunTask";
import {runApi} from "../api/runApi";
import {DataById} from "../redux/map/types";
import {SimulationState} from "../api/entities/replancity_RunnedAlgorithm";
import type * as schema from '../api/schema';


export type Run = {
    id: string;
    title: string;
    state?: SimulationState;
} & schema.definitions["entities_replancity_RunTask"];

export const RunEntityName = 'replancity_RunTask';

export type PresetEntityName =
    | 'replancity_PublicTransitPreset'
    | 'replancity_RoadInfrastructurePreset'
    | 'replancity_MaasPreset'
    | 'replancity_EvPreset'
    | 'replancity_ScenarioCasePreset';

export type PresetTypeKey =
    | 'roadPreset'
    | 'publicTransit'
    | 'maasPreset'
    | 'evPreset'
    | 'scenarioCasePreset';

export const PRESET_BY_ENTITY_NAME_KEY: Record<PresetEntityName, PresetTypeKey> = {
    replancity_RoadInfrastructurePreset: 'roadPreset',
    replancity_PublicTransitPreset: 'publicTransit',
    replancity_MaasPreset: 'maasPreset',
    replancity_EvPreset: 'evPreset',
    replancity_ScenarioCasePreset: 'scenarioCasePreset'
};

export type CreationPresetMetadataName =
    | 'replancity_RoadPresetCreateRequest'
    | 'replancity_TransitPresetCreateRequest'
    | 'replancity_ElectricPresetCreateRequest';

export type CreationPreset = {
    metaDataName: CreationPresetMetadataName;
    presetName: PresetEntityName;
    printedName: string;
}

export type AddPresetForm = {
    serviceName: string;
    data: {
        sourcePresetId?: string;
        presetName: string;
    }
}

const PresetsContext = createContext({
    runsLoading: false,
    presetsLoading: false,
    loadRuns: (selectedPresetEntityName?: PresetEntityName, additionalRuns?: schema.definitions["entities_replancity_RunTask"][]): Promise<void> => {
        return Promise.resolve();
    },
    getRunParams: (runId: string): Promise<RunTaskEntity> => {
        return Promise.resolve({} as RunTaskEntity);
    },
    pinRun: (runId: string, isPinned: boolean): Promise<void> => {
        return Promise.resolve();
    },
    loadPresetTypes: (): Promise<CreationPreset[]> => {
        return Promise.resolve([]);
    },
    loadPresets: (presetEntityName: PresetEntityName): Promise<void> => {
        return Promise.resolve();
    },
    runs: [] as schema.definitions["entities_replancity_RunTask"][],
    runsById: {},
    runsWithoutAdditional: [] as schema.definitions["entities_replancity_RunTask"][],
    filteredRuns: [] as schema.definitions["entities_replancity_RunTask"][],
    presets: [] as ProjectPreset[],
    presetsById: {},
    defaultPresetId: '',
    selectedRunId: '',
    selectedPresetId: '',
    selectedPresetEntityName: '' as PresetEntityName,
    getPresetFromSessionStorage: (presetEntityName: string): string | undefined => {
        return '';
    },
    addPreset: (presetEntityName: PresetEntityName, args: AddPresetForm) => {
        return {} as Promise<PublicTransitPresetEntity | ApiError>;
    },
    selectPreset: (id: string, presetEntityName: PresetEntityName) => {
        return;
    },
    selectRun: (id: string) => {
        return;
    },
    cleanSelectedPreset: () => {
        return;
    },
    cleanPresets: () => {
        return;
    },
    cleanSelectedRun: () => {
        return;
    },
    cleanRuns: () => {
        return;
    }
});

const usePresetsContext = () => {
    return useContext(PresetsContext);
};

const enum PresetsStorageKeys {
    EntityNameById = "Replan.presetEntityNameById",
}

const PresetsProvider = ({children}) => {
    const [presetsLoading, setPresetsLoading] = useState<boolean>(false);
    const [runsLoading, setRunsLoading] = useState<boolean>(false);
    const projectId = useTypedSelector(selectCurrentProjectId);
    const runs = useTypedSelector(selectProjectRuns);
    const runsById = useTypedSelector(selectRunsById);
    const [runsWithoutAdditional, setRunsWithoutAdditional] = useState<schema.definitions["entities_replancity_RunTask"][]>([]);
    const presets = useTypedSelector(selectProjectPresets);
    const presetsById = useTypedSelector(selectPresetsById);
    const selectedRunId = useTypedSelector(selectSelectedRunId);
    const selectedPresetId = useTypedSelector(selectSelectedPresetId);
    const selectedPresetEntityName = useTypedSelector(selectPresetType);
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const abortControllerRef = useRef(new AbortController());

    useEffect(() => {
        return () => {
            cleanPresets();
            cleanRuns();
        }
    }, []);

    const runIdQueryParam = searchParams.get('runId');

    const isRunRelatedToPreset = useCallback((run: Run, selectedPresetId: string, selectedPresetEntityName: PresetEntityName) => {
        const presetKey = PRESET_BY_ENTITY_NAME_KEY[selectedPresetEntityName];
        return run[presetKey]?.id === selectedPresetId;
    }, [])

    const filteredRuns = useMemo(() => {
        //selectedPresetId === '' for example on project page. there isn't presets selector => all runs should be presented in selector
        return runs.filter(run => selectedPresetId ? isRunRelatedToPreset(run, selectedPresetId, selectedPresetEntityName) : true);
    }, [runs, selectedPresetId, selectedPresetEntityName, isRunRelatedToPreset])

    const filteredRunsById = useMemo(() => {
        return filteredRuns.reduce((acc, run) => {
            if (!acc[run.id]) {
                acc[run.id] = run;
            }
            return acc;
        }, {});
    }, [filteredRuns])

    const getPresetFromSessionStorage = useCallback((presetEntityName: string): string | undefined => {
        const presets: string = getSessionStorageItem(PresetsStorageKeys.EntityNameById);
        return presets[presetEntityName];
    }, [])

    const defaultPresetId = useMemo(() => {
        return presets.find(({defaultPreset}) => !!defaultPreset)?.id ?? presets[0]?.id;
    }, [presets])

    const savePresetToSessionStorage = useCallback((presetId: string, presetEntityName: string): void => {

        let presetEntityNameById = getSessionStorageItem(PresetsStorageKeys.EntityNameById);
        if (!presetEntityNameById) {
            presetEntityNameById = {};
        }
        presetEntityNameById[presetEntityName] = presetId;

        sessionStorage.setItem('Replan.presetEntityNameById', JSON.stringify(presetEntityNameById));
    }, [])

    const loadRuns = useCallback(async (selectedPresetEntityName?: PresetEntityName, additionalRuns?: schema.definitions["entities_replancity_RunTask"][]) => {
        setRunsLoading(true);

        const resp = await runApi.getRuns({
            projectId,
            abortSignal: abortControllerRef.current.signal
        });

        if (!isErrorResponse(resp)) {
            const ids: string[] = [];
            const presetKey = selectedPresetEntityName ? PRESET_BY_ENTITY_NAME_KEY[selectedPresetEntityName] : '';

            const runs = resp.filter(run => presetKey ? run[presetKey] : true);
            setRunsWithoutAdditional(runs);

            const byId: DataById<Run> =
                (additionalRuns ?? [])
                    .concat(runs)
                    .reduce((acc, {id, name, state, isPinned, ...rest}) => {
                        acc[id!] = {
                            id,
                            name,
                            state,
                            isPinned,
                            ...rest
                        };

                        ids.push(id!);

                        return acc;
                    }, {}) as DataById<Run>;

            dispatch(addedRuns({byId, ids}));
        }

        setRunsLoading(false);
    }, [projectId])

    const getRunParams = useCallback(async (runId: string): Promise<RunTaskEntity> => {
        return await runApi.getRunParams({
            runId,
            abortSignal: abortControllerRef.current.signal
        });
    }, [])

    const pinRun = useCallback(async (runId: string, isPinned: boolean) => {
        setRunsLoading(true);

        const abortController = new AbortController();
        const resp = await entityApi.saveEntity<schema.definitions["entities_replancity_RunTask"]>({
            entityName: RunEntityName,
            data: {
                id: runId,
                isPinned
            },
            abortSignal: abortController.signal
        });

        await loadRuns(selectedPresetEntityName);

        setRunsLoading(false);
    }, [loadRuns, selectedPresetEntityName])

    const loadPresets = useCallback(async (presetEntityName: PresetEntityName) => {
        setPresetsLoading(true);

        const resp: PublicTransitPresetEntity[] = await presetsApi.getPresets({
            projectId,
            presetEntityName,
            abortSignal: abortControllerRef.current.signal
        });

        if (!isErrorResponse(resp)) {
            const ids: string[] = [];
            const byId: DataById<ProjectPreset> = resp.reduce((acc, {id, name, defaultPreset}) => {
                acc[id!] = {
                    id,
                    title: name,
                    defaultPreset
                };

                ids.push(id!);

                return acc;
            }, {});

            dispatch(addedPresets({byId, ids}));
        }

        setPresetsLoading(false);
    }, [projectId])

    const loadPresetTypes = useCallback(async () => {
        const abortController = new AbortController();
        return await presetsApi.getPresetsTypes({abortSignal: abortController.signal});
    }, [projectId])

    const cleanSelectedPreset = useCallback(() => {
        dispatch(setSelectedPreset());
    }, []);

    const cleanSelectedRun = useCallback(() => {
        dispatch(setSelectedRunId());
        setSearchParams(prevSearchParams => updateSearchParam({prevSearchParams, paramsToDelete: 'runId'}));
    }, [updateSearchParam]);

    const cleanPresets = useCallback(() => {
        dispatch(addedPresets());
        cleanSelectedPreset();
        localStorage.removeItem(PresetsStorageKeys.EntityNameById);
        sessionStorage.removeItem(PresetsStorageKeys.EntityNameById);
        setSearchParams(prevSearchParams => updateSearchParam({
            prevSearchParams,
            paramsToDelete: ['presetId', 'runId']
        }));
    }, [cleanSelectedPreset]);

    const cleanRuns = useCallback(() => {
        cleanSelectedRun();
        dispatch(addedRuns());
        setSearchParams(prevSearchParams => updateSearchParam({prevSearchParams, paramsToDelete: 'runId'}));
    }, [cleanSelectedRun]);

    const selectPreset = useCallback((id: string, presetEntityName: PresetEntityName) => {
        savePresetToSessionStorage(id, presetEntityName);

        setSearchParams(prevSearchParams => updateSearchParam({
            prevSearchParams,
            paramsToSet: {'presetId': id}
        }));

        dispatch(setSelectedPreset({presetId: id, presetType: presetEntityName}));
    }, [setSearchParams, savePresetToSessionStorage]);

    const selectRun = useCallback((id: string) => {
        setSearchParams(prevSearchParams => updateSearchParam({prevSearchParams, paramsToSet: {runId: id}}));
        dispatch(setSelectedRunId(id));
    }, [setSearchParams]);

    const addPreset = useCallback(async (presetEntityName: PresetEntityName, args: AddPresetForm): Promise<PublicTransitPresetEntity | ApiError> => {
        setPresetsLoading(true);

        let resp;
        if (args.data['sourcePresetId'] && args['serviceName']) {
            resp = await presetsApi.copyPreset({
                ...args,
                abortSignal: abortControllerRef.current.signal
            } as CopyPresetFnArgs);
        } else {
            const {presetName, ...restData} = (args).data;

            resp = await entityApi.saveEntity<typeof presetEntityName>({
                entityName: presetEntityName,
                data: {
                    project: {
                        id: projectId
                    },
                    ...restData,
                    name: presetName
                },
                abortSignal: abortControllerRef.current.signal
            });
        }

        if (!isErrorResponse(resp)) {
            await loadPresets(presetEntityName);

            const {id} = resp;
            selectPreset(id, presetEntityName);
        }
        setPresetsLoading(false);

        return resp;
    }, [projectId])

    useEffect(() => {
        if (filteredRuns.length) {
            if (runIdQueryParam && filteredRunsById?.[runIdQueryParam]) {
                selectRun(runIdQueryParam);
            } else if (filteredRuns.length) {
                selectRun(filteredRuns[0].id!);
            }
        } else {
            cleanSelectedRun();
        }
    }, [filteredRuns, filteredRunsById, runIdQueryParam, selectRun, cleanSelectedRun]);

    const value = {
        presetsLoading,
        runsLoading,
        loadRuns,
        getRunParams,
        pinRun,
        loadPresetTypes,
        loadPresets,
        runs,
        runsById,
        runsWithoutAdditional,
        filteredRuns,
        presets,
        presetsById,
        defaultPresetId,
        getPresetFromSessionStorage,
        selectedRunId,
        selectedPresetId,
        selectedPresetEntityName,
        addPreset,
        selectPreset,
        selectRun,
        cleanSelectedPreset,
        cleanPresets,
        cleanSelectedRun,
        cleanRuns
    };

    return (
        <PresetsContext.Provider value={value}>
            {children}
        </PresetsContext.Provider>
    );
};

export {PresetsProvider, usePresetsContext};